import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { ButtonMobile } from '@alfa-bank/site-button-v2/mobile';

import type { TTooltip } from './models';

export const Tooltip: FC<TTooltip> = ({
    CoreTooltip,
    Stack,
    actionButtonTitle = 'Понятно',
    anchor,
    arrowClassName,
    autoWidth = false,
    availableHeight,
    bottomSheetProps,
    children,
    className,
    colors,
    contentClassName,
    dataTestId,
    display,
    fallbackPlacements,
    getPortalContainer,
    modalClassName = '',
    offset,
    onClose,
    onCloseDelay,
    onOpen,
    onOpenDelay,
    open,
    popoverClassName,
    position,
    preventOverflow,
    stackingOrder,
    styles,
    tag,
    targetClassName,
    targetRef,
    trigger,
    updatePopover,
    useAnchorWidth,
    useNamedContainersWithChildren,
    view,
    ...rest
}) => {
    const isOpenPropExist = typeof open === 'boolean';

    const [isOpen, setIsOpen] = useState<boolean | undefined>(undefined);

    const { children: childrenContent, content } = useNamedContainersWithChildren(children);

    const Tag = tag || 'div';

    const handleOpen = () => {
        if (!isOpenPropExist) {
            setIsOpen(true);
        }

        onOpen?.();
    };

    const handleClose = () => {
        if (!isOpenPropExist) {
            setIsOpen(false);
        }

        onClose?.();
    };

    useEffect(() => {
        setIsOpen(isOpenPropExist ? undefined : false);
    }, [isOpenPropExist]);

    return (
        <Tag className={className}>
            <Stack value={stackingOrder.POSITIONER}>
                {zIndex => (
                    <CoreTooltip
                        {...rest}
                        anchor={anchor}
                        arrowClassName={arrowClassName}
                        availableHeight={availableHeight}
                        bottomSheetProps={{
                            modalClassName: cn(styles.external, styles.modalClassName),
                            actionButton: (
                                <ButtonMobile block onClick={handleClose} size="s" view="secondary">
                                    {actionButtonTitle}
                                </ButtonMobile>
                            ),
                            ...bottomSheetProps,
                        }}
                        colors={colors}
                        content={content}
                        contentClassName={contentClassName}
                        dataTestId={dataTestId}
                        fallbackPlacements={fallbackPlacements}
                        getPortalContainer={getPortalContainer}
                        modalClassName={cn(modalClassName, styles.external, styles.modalClassName)}
                        offset={offset}
                        onClose={handleClose}
                        onCloseDelay={onCloseDelay}
                        onOpen={handleOpen}
                        onOpenDelay={onOpenDelay}
                        open={isOpen ?? open}
                        popoverClassName={popoverClassName}
                        position={position}
                        preventOverflow={preventOverflow}
                        targetClassName={cn(
                            styles.external,
                            styles.tooltipTarget,
                            targetClassName,
                            {
                                [styles.inlineWrapper]: display === 'inline-block' || !autoWidth,
                                [styles.autoWidth]: autoWidth,
                                [styles.inline]: display === 'inline',
                            }
                        )}
                        targetRef={targetRef}
                        trigger={trigger}
                        updatePopover={updatePopover}
                        useAnchorWidth={useAnchorWidth}
                        view={view}
                        zIndex={zIndex}
                    >
                        <Tag>{childrenContent}</Tag>
                    </CoreTooltip>
                )}
            </Stack>
        </Tag>
    );
};
